import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { trackCTA } from "@/lib/analytics";

const NAV_LINKS = [
  { href: "#how", label: "How it works" },
  { href: "#features", label: "Features" },
  { href: "#pricing", label: "Pricing" },
  { href: "#faq", label: "FAQ" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-black/70 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <a href="#top" data-testid="navbar-logo" className="flex items-center gap-2 group">
          <div className="w-7 h-7 bg-[#D4FF00] flex items-center justify-center rounded-sm">
            <span className="font-display text-black text-sm">ID</span>
          </div>
          <span className="font-display uppercase tracking-tight text-sm">
            Influence<span className="text-[#D4FF00]">Detector</span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`navlink-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-sm text-[#A1A1AA] hover:text-white transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href="https://app.influencedetector.com/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-login-btn"
            onClick={() => trackCTA("login", "navbar")}
            className="text-sm font-semibold text-white/70 hover:text-white transition-colors px-3 py-1.5 border border-white/15 rounded-sm hover:border-white/40"
          >
            Log in
          </a>
          <Button
            data-testid="navbar-install-btn"
            onClick={() => {
              trackCTA("signup_free", "navbar");
              document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="btn-lime rounded-sm h-9 px-4 text-xs hover:text-black"
          >
            Sign up Free
          </Button>
        </div>

        <button
          data-testid="navbar-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          className="md:hidden w-9 h-9 flex items-center justify-center border border-white/10 rounded-sm"
          aria-label="Toggle menu"
        >
          {open ? <X size={16} /> : <Menu size={16} />}
        </button>
      </div>

      {open && (
        <div data-testid="navbar-mobile-menu" className="md:hidden bg-black/95 border-t border-white/5 px-4 py-6 flex flex-col gap-4">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className="text-base text-[#A1A1AA] hover:text-white"
            >
              {l.label}
            </a>
          ))}
          <a
            href="https://app.influencedetector.com/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-mobile-login-btn"
            onClick={() => setOpen(false)}
            className="mt-2 text-center border border-white/15 rounded-sm py-3 text-sm font-semibold text-white/80 hover:text-white hover:border-white/40 transition-colors"
          >
            Log in
          </a>
          <Button
            data-testid="navbar-mobile-install-btn"
            onClick={() => {
              setOpen(false);
              document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="btn-lime rounded-sm hover:text-black"
          >
            Sign up Free
          </Button>
        </div>
      )}
    </header>
  );
}
